import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Badge,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import { END_POINT } from "../../../constants/defaultValues";
import { Separator } from "../../../components/common/CustomBootstrap";
import FooterPage from "../../../containers/pages/FooterPage";
import IntlMessages from "../../../helpers/IntlMessages";
import Breadcrumb from "../../../containers/navs/Breadcrumb";
import { injectIntl } from "react-intl";
import {
  StockReturnForm,
  StockReturnList,
  StockReturnApplicationMenu,
} from "../../../containers/pages/stock";

const StockReturnPage = ({ match, intl }) => {
  const [stocks, setStocks] = useState([]);
  const [providers, setProviders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const loadStocks = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/stock/all-unit`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setStocks(response.data.data);
      }
    } catch (error) {
      console.error("Error loading stocks:", error);
    }
  };

  const loadProviders = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/company-provider`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setProviders(response.data.data);
      }
    } catch (error) {
      console.error("Error loading providers:", error);
    }
  };

  const loadReturns = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/api/stock-return`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setReturns(response.data.data);
        setFilteredReturns(response.data.data);
      }
    } catch (error) {
      console.error("Error loading returns:", error);
    }
  };

  useEffect(() => {
    loadStocks();
    loadProviders();
    loadReturns();
  }, [refreshKey]);

  // Apply filter
  useEffect(() => {
    if (!filter || filter.type === "all") {
      setFilteredReturns(returns);
      return;
    }

    let filtered = [...returns];

    if (filter.type === "status") {
      filtered = returns.filter((item) => item.status === filter.value);
    } else if (filter.type === "month") {
      const startOfMonth = moment()
        .subtract(filter.value, "months")
        .startOf("month");
      const endOfMonth = moment()
        .subtract(filter.value, "months")
        .endOf("month");
      filtered = returns.filter((item) => {
        const itemDate = moment(item.createdAt);
        return itemDate.isBetween(startOfMonth, endOfMonth, null, "[]");
      });
    }

    setFilteredReturns(filtered);
  }, [filter, returns]);

  const handleSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleViewDetail = (item) => {
    setSelectedReturn(item);
    setShowDetail(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(value || 0);
  };

  return (
    <div className="app-row disable-text-selection">
      <div className="container-fluid">
        <Row className="mb-4">
          <Col>
            <Breadcrumb heading="menu.stock-return" match={match} />
            <p className="text-muted">
              <IntlMessages id="return.title" />
            </p>
          </Col>
          <Col className="text-right">
            <Button color="primary" onClick={() => setShowForm(true)}>
              <i className="simple-icon-plus mr-2"></i>
              <IntlMessages id="return.button.create" />
            </Button>
          </Col>
        </Row>

        <StockReturnList
          data={filteredReturns}
          key={refreshKey}
          onViewDetail={handleViewDetail}
        />

        <StockReturnForm
          isOpen={showForm}
          toggle={() => setShowForm(false)}
          stocks={stocks}
          providers={providers}
          onSuccess={handleSuccess}
        />

        {/* Modal Chi tiết Hoàn hàng */}
        <Modal
          isOpen={showDetail}
          toggle={() => setShowDetail(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowDetail(false)}>
            <IntlMessages id="return.modal.detail.title" /> -{" "}
            {selectedReturn?.return_code}
          </ModalHeader>
          <ModalBody>
            {selectedReturn && (
              <>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <strong>
                      <IntlMessages id="return.date" />:
                    </strong>{" "}
                    {moment(selectedReturn.return_date).format("DD/MM/YYYY")}
                  </div>
                  <div>
                    <Badge
                      color={
                        selectedReturn.status === "completed"
                          ? "success"
                          : "warning"
                      }
                    >
                      {selectedReturn.status === "completed" ? (
                        <IntlMessages id="return.status.completed" />
                      ) : (
                        <IntlMessages id="return.status.pending" />
                      )}
                    </Badge>
                  </div>
                </div>
                {selectedReturn.reason && (
                  <p>
                    <strong>
                      <IntlMessages id="return.reason" />:
                    </strong>{" "}
                    {selectedReturn.reason}
                  </p>
                )}
                {selectedReturn.notes && (
                  <p className="text-muted">
                    <strong>
                      <IntlMessages id="audit.note" />:
                    </strong>{" "}
                    {selectedReturn.notes}
                  </p>
                )}
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table size="sm" hover>
                    <thead>
                      <tr>
                        <th>
                          <IntlMessages id="return.table.stock" />
                        </th>
                        <th className="text-center">
                          <IntlMessages id="return.table.quantity" />
                        </th>
                        <th className="text-right">
                          <IntlMessages id="return.table.price" />
                        </th>
                        <th>
                          <IntlMessages id="return.table.provider" />
                        </th>
                        <th className="text-right">
                          <IntlMessages id="return.table.total" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReturn.items?.map((item) => {
                        const providerName =
                          providers.find((p) => p.id === item.provider_id)
                            ?.name || "-";
                        return (
                          <tr key={item.id}>
                            <td style={{ verticalAlign: "middle" }}>
                              {item.stock?.name}
                            </td>
                            <td
                              className="text-center"
                              style={{ verticalAlign: "middle" }}
                            >
                              {item.quantity}
                            </td>
                            <td
                              className="text-right"
                              style={{ verticalAlign: "middle" }}
                            >
                              {formatCurrency(item.unit_price)}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {providerName}
                            </td>
                            <td
                              className="text-right"
                              style={{ verticalAlign: "middle" }}
                            >
                              {formatCurrency(item.quantity * item.unit_price)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3 pt-3 border-top">
                  <strong>
                    <IntlMessages id="return.total-value" />:
                  </strong>{" "}
                  <span className="text-danger">
                    {formatCurrency(selectedReturn.total_value)}
                  </span>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>

      <StockReturnApplicationMenu
        data={returns}
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      <Separator />
      <FooterPage />
    </div>
  );
};

export default injectIntl(StockReturnPage);
